import type { paths as Ops } from '@cortex/sdk-guides-client';

// Response shapes of the guides API, from the generated client (never hand-written).
type G = '/guides';
type Json<P extends keyof Ops, M extends keyof Ops[P], C extends number = 200> = Ops[P][M] extends {
  responses: { [K in C]: { content: { 'application/json': infer R } } };
}
  ? R
  : never;

export type GuideSummary = Json<G, 'get'>['guides'][number];
export type Guide = Json<`${G}/{key}`, 'get'>;
export type GuideSection = Guide['sectionList'][number];
export type GuideAsset = Guide['assetList'][number];
export type GuideExport = Json<`${G}/{key}/export`, 'post', 201>;

/** A section being edited: `id` is absent until it is saved; `uid` keys it in the list. */
export interface DraftSection {
  uid: string;
  id?: number;
  heading: string;
  body: string;
}

let seq = 0;
export const draftOf = (s: { id?: number; heading: string; body: string }): DraftSection => ({
  uid: `s${++seq}`,
  ...(s.id !== undefined ? { id: s.id } : {}),
  heading: s.heading,
  body: s.body,
});

/** Moves the item at `from` by `by` places (clamped). */
export function move<T>(list: readonly T[], from: number, by: number): T[] {
  const to = Math.max(0, Math.min(list.length - 1, from + by));
  if (to === from) return [...list];
  const out = [...list];
  const [it] = out.splice(from, 1);
  out.splice(to, 0, it!);
  return out;
}

/** Groups guides by the app they explain; guides of no one app come last. */
export function groupGuides(
  guides: readonly GuideSummary[],
): { label: string; app: string | null; items: GuideSummary[] }[] {
  const by = new Map<string | null, GuideSummary[]>();
  for (const g of guides) by.set(g.app ?? null, [...(by.get(g.app ?? null) ?? []), g]);
  const apps = [...by.keys()].filter((k): k is string => !!k).sort();
  const out = apps.map((a) => ({ label: a, app: a as string | null, items: by.get(a)! }));
  if (by.has(null)) out.push({ label: 'Project', app: null, items: by.get(null)! });
  return out;
}

export const pdfLabel = (e: { pages: number | null; createdAt: string } | null | undefined): string => {
  if (!e) return '';
  const when = new Date(e.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  return [e.pages ? `${e.pages} page${e.pages === 1 ? '' : 's'}` : '', `exported ${when}`]
    .filter(Boolean)
    .join(' · ');
};

/** base64 of a file, for the JSON upload body. */
export async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

/** A plain asset name from a file name. */
export const assetNameOf = (filename: string) =>
  filename
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^[^A-Za-z0-9]+/, '')
    .slice(0, 120) || 'image.png';
